import { CardContent, TextField } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { IEBlockMaterial } from '../../../models/eBlockMaterial.model';
import { AuthState } from '../../../state/auth';
import { AppState } from '../../../state/configureStore';
import {
  clearEBlockMaterial,
  createEBlockMaterial,
  EBlockMaterialState,
  fetchEBlockMaterial,
  updateEBlockMaterial,
} from '../../../state/eBlockMaterial';
import {
  FixedPositionButtons,
  Loading,
  PageContainer,
  PageTitle,
} from '../../components';

const EBlockMaterialSchema = Yup.object().shape({
  code: Yup.string()
    .strict(true)
    .trim()
    .max(3, 'Code is exceeded character limit(3)')
    .required('Code is required'),
  description: Yup.string().nullable(true),
});

interface MaterialFormRouteParamsProps {
  id: string;
}

interface MaterialFormProps extends RouteComponentProps<MaterialFormRouteParamsProps> {}

const EBlockMaterialForm: React.FC<MaterialFormProps> = props => {
  const dispatch = useDispatch();
  const { loading: authIsLoading } = useSelector<AppState, AuthState>(
    state => state.auth,
  );
  const { match, history } = props;
  const {
    params: { id: paramsId },
  } = match;

  const isUpdateMode = paramsId !== undefined;

  const { item: material, loading: materialIsLoading } = useSelector<
    AppState,
    EBlockMaterialState
  >(state => state.eBlockMaterial);

  const isLoading = authIsLoading || materialIsLoading;

  const defaultValues: IEBlockMaterial = {};

  const handleSubmit = (
    values: IEBlockMaterial,
    actions: FormikHelpers<IEBlockMaterial>,
  ) => {
    if (isUpdateMode) {
      dispatch(updateEBlockMaterial({ values, history }));
    } else {
      dispatch(createEBlockMaterial({ values, history }));
    }
    actions.setSubmitting(false);
  };

  const redirectToSalesGroupList = () => {
    history.goBack();
  };

  useEffect(() => {
    if (isUpdateMode) {
      dispatch(fetchEBlockMaterial(+paramsId));
    } else {
      dispatch(clearEBlockMaterial());
    }
    return function cleanUp() {
      dispatch(clearEBlockMaterial());
    };
  }, [isUpdateMode, paramsId, dispatch]);

  return (
    <PageContainer>
      <Loading isLoading={isLoading}>
        <Formik
          enableReinitialize={true}
          initialValues={{ ...defaultValues, ...material }}
          validationSchema={EBlockMaterialSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched, submitForm }) => {
            return (
              <Form>
                <PageTitle>
                  {!isUpdateMode
                    ? `New EBlock Material`
                    : `Update EBlock Material : ${material.code}`}
                </PageTitle>
                <CardContent>
                  <TextField
                    name="code"
                    type="input"
                    label="Code"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={values.code || ''}
                    onChange={handleChange}
                    error={!!errors.code && !!touched.code}
                    helperText={errors.code}
                    InputProps={{
                      readOnly: isUpdateMode,
                    }}
                  />
                  <TextField
                    name="description"
                    type="input"
                    label="Description"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={values.description || ''}
                    onChange={handleChange}
                    error={!!errors.description && !!touched.description}
                    helperText={errors.description}
                  />
                </CardContent>
                <FixedPositionButtons
                  actions={[
                    {
                      name: !isUpdateMode ? `Create` : `Update`,
                      onClick: () => {
                        submitForm();
                      },
                      color: 'primary' as 'primary',
                      disabled: false,
                      icon: 'save',
                    },
                    {
                      name: 'CANCEL',
                      onClick: redirectToSalesGroupList,
                      color: 'default' as 'default',
                      disabled: false,
                    },
                  ]}
                  open
                />
              </Form>
            );
          }}
        </Formik>
      </Loading>
    </PageContainer>
  );
};
export default EBlockMaterialForm;
